import React, { createContext, useContext, ReactNode, useCallback } from 'react';
import { toast, ToastOptions, Id } from 'react-toastify';
import { Box, Typography } from '@mui/material';

interface SnackBarContextType {
  showMessage: (
    message: string | ReactNode, // <- support string or JSX
    level?: 'info' | 'error' | 'warning' | 'success',
    options?: { override?: boolean; id?: Id; toastOptions?: ToastOptions }
  ) => void;
}

const SnackBarContext = createContext<SnackBarContextType | undefined>(undefined);

interface SnackBarProviderProps {
  children: ReactNode;
}

export const SnackBarProvider = ({ children }: SnackBarProviderProps) => {
  const showMessage: SnackBarContextType['showMessage'] = useCallback(
    (message, level = 'success', options = { override: false, id: undefined, toastOptions: {} }) => {
      const { override, id, toastOptions } = options;
      const toastId = override ? id ?? level : undefined;

      const config: ToastOptions = {
        toastId,
        ...toastOptions,
      };

      // Detect if message is string (legacy) or JSX (new)
      const content = typeof message === 'string' ? (
        <Typography>{message}</Typography>
      ) : (
        message
      );

      switch (level) {
        case 'info':
          toast.info(content, config);
          break;
        case 'error':
          toast.error(content, config);
          break;
        case 'warning':
          toast.warning(content, config);
          break;
        case 'success':
          toast.success(content, config);
          break;
      }
    },
    []
  );

  return <SnackBarContext.Provider value={{ showMessage }}>{children}</SnackBarContext.Provider>;
};

export const useSnackBar = (): SnackBarContextType => {
  const context = useContext(SnackBarContext);
  if (!context) {
    throw new Error('useSnackBar must be used within a SnackBarProvider');
  }
  return context;
};
